import { createContext, useCallback, useContext, useMemo, useState } from 'react';

const Ctx = createContext(null);
const STORAGE_KEY = 'betohalics.staff_alerts.v1';
const MAX_ALERTS = 10;

const safeRead = () => {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        return raw ? JSON.parse(raw) : [];
    } catch { return []; }
};

const safeWrite = (alerts) => {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(alerts)); } catch { /* quota / private mode */ }
};

const newId = () => {
    try { return crypto.randomUUID(); } catch {
        return `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    }
};

/**
 * Persistent bell-tray for staff alerts. Mirrors every actionable toast that
 * fires across the cashier + admin dashboards so nothing slips off-screen
 * when the operator looks away. Backed by localStorage (capped at 10) so
 * alerts survive navigation. New alerts are inserted at the top and
 * marked unread until the user opens the bell.
 *
 * Alert shape:
 *   { id, level: 'info'|'success'|'warning'|'error',
 *     icon: string, title: string, description?: string,
 *     userId?: string, ts: number, read: boolean }
 */
export const StaffAlertsProvider = ({ children }) => {
    const [alerts, setAlerts] = useState(safeRead);

    const push = useCallback((alert) => {
        if (!alert || !alert.title) return;
        const item = {
            id: alert.id || newId(),
            level: alert.level || 'info',
            icon: alert.icon || '🔔',
            title: alert.title,
            description: alert.description || '',
            userId: alert.userId || null,
            ts: Date.now(),
            read: false,
        };
        setAlerts((prev) => {
            const filtered = prev.filter((a) => a.id !== item.id);
            const next = [item, ...filtered].slice(0, MAX_ALERTS);
            safeWrite(next);
            return next;
        });
    }, []);

    const markAllRead = useCallback(() => {
        setAlerts((prev) => {
            const next = prev.map((a) => (a.read ? a : { ...a, read: true }));
            safeWrite(next);
            return next;
        });
    }, []);

    const clear = useCallback(() => {
        setAlerts([]);
        safeWrite([]);
    }, []);

    const unreadCount = useMemo(() => alerts.filter((a) => !a.read).length, [alerts]);

    const value = useMemo(() => ({ alerts, push, markAllRead, clear, unreadCount }),
        [alerts, push, markAllRead, clear, unreadCount]);

    return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
};

export const useStaffAlerts = () => {
    const v = useContext(Ctx);
    // Soft-fail if a component imports this outside the provider — return
    // a no-op stub so we never crash the parent dashboard. Bell will just
    // show 0.
    if (!v) {
        return { alerts: [], push: () => {}, markAllRead: () => {}, clear: () => {}, unreadCount: 0 };
    }
    return v;
};
